import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import OptimizedImage from '@/components/OptimizedImage';
import siteLogo from '@/assets/organisatieopstellingen-logo.png';
import siteLogoWebp from '@/assets/organisatieopstellingen-logo.webp';
import { LEERGANG_SHORT_LABEL, SITE_NAME } from '@/seo/siteConfig';

const navigation = [
  {
    name: 'Organisatieopstellingen',
    href: '/organisatieopstellingen',
  },
  {
    name: LEERGANG_SHORT_LABEL,
    href: '/leergang-onderstroom',
  },
  {
    name: 'Aanbod',
    href: '/aanbod',
  },
  {
    name: 'Agenda',
    href: '/agenda',
  },
  {
    name: 'Over',
    href: '/over',
  },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open and close on Escape
  useEffect(() => {
    const body = document.body;
    if (isOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle hash navigation on page load and location change
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled ? 'bg-[#FAEBEE]/90 backdrop-blur-md py-3 shadow-sm' : 'bg-transparent py-6'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center hover-lift relative z-50">
            <OptimizedImage 
              src={siteLogo} 
              webpSrc={siteLogoWebp} 
              alt={SITE_NAME} 
              className="h-8 w-auto object-contain" 
              width={120} 
              height={32} 
              priority={true} 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 xl:gap-7">
            {navigation.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`text-[11px] lg:text-xs xl:text-sm font-medium uppercase tracking-wide flex items-center gap-1 hover:text-[#854C73] transition-colors whitespace-nowrap ${
                  isActive(item.href) ? 'text-[#854C73] font-bold' : 'text-[#6b4912]'
                }`}
              >
                {item.name}
              </Link>
            ))}
            <Link to="/contact">
              <Button
                variant="default"
                size="sm"
                className="ml-1 lg:ml-3 rounded-full bg-[#854C73] text-[#F9EBEF] hover:bg-[#590D0D] hover:scale-105 transition-all"
              >
                Contact
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-[#854C73] z-50"
            aria-label={isOpen ? 'Sluit menu' : 'Open menu'}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[98] md:hidden"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Menu Panel */}
            <div className="fixed inset-0 bg-[#FAEBEE] z-[99] flex flex-col items-center justify-center gap-6 animate-fade-in p-6 overflow-y-auto md:hidden">
              {/* Header */}
              <div className="flex items-center justify-between w-full max-w-xs mb-4">
                <OptimizedImage 
                  src={siteLogo} 
                  webpSrc={siteLogoWebp} 
                  alt={SITE_NAME} 
                  className="h-10 w-auto object-contain" 
                  width={120} 
                  height={40} 
                  priority={true} 
                />
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-[#854C73]"
                >
                  <X size={28} />
                </button>
              </div>

              {/* Menu content */}
              <div className="w-full max-w-xs">
                {navigation.map((item) => (
                  <div key={item.name} className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                    <Link
                      to={item.href}
                      onClick={() => setIsOpen(false)}
                      className={`block w-full py-2 ${
                        isActive(item.href) ? 'text-[#854C73] font-bold' : 'text-[#6b4912]'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </div>
                ))}
                
                {/* Contact Button */}
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="block w-full"
                  >
                    <Button 
                      variant="default" 
                      size="lg" 
                      className="w-full bg-[#854C73] text-[#F9EBEF] hover:bg-[#590D0D] rounded-full"
                    >
                      Contact
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
